<!--
  What a registered member sees in the ticket instead of the call to action:
  their state (registered, on the waitlist, payment open), their guests, and
  the ways to change it — edit names, add guests, cancel — all inline.
-->
<script>
  import { getContext } from 'svelte';
  import { page } from '$app/state';
  import t, { LANG } from '#lib/i18n.svelte.js';
  import { format_date, format_price, get_registration_payment_state } from '#lib/util.js';
  import { submit_registration_changes, get_attendee_errors, translate_attendee_errors } from '#lib/event_registration_client.js';
  import EventRegistrationForm from '#lib/EventRegistrationForm.svelte';
  import TextInput from '#lib/TextInput.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import EventChip from '#lib/EventChip.svelte';
  import '#lib/system-styles/event-ui.css';

  let {
    entry,
    /** The member's own registrations that are not refunded */
    registrations,
    capacity,
    is_restricted = false,
    has_concluded = false,
    /** The member is back from Checkout and the entry is confirming the payment */
    confirming_payment = false,
    /** Called with {added, changed, cancelled} after a successful change */
    onchanged,
    /** Refetches the registrations after a change that only partly went through */
    onrefresh,
  } = $props();

  const site_settings = getContext('site_settings');

  /** 'view' | 'edit' | 'add' | 'confirm_cancel' */
  let mode = $state('view');
  let busy = $state(false);
  let error = $state('');
  let edit_rows = $state([]);

  let currency = $derived(entry.currency_code);
  let pending_rows = $derived(registrations.filter(reg => reg.status === 'pending'));
  let all_waitlisted = $derived(registrations.every(reg => reg.status === 'waitlist'));
  // A paid seat can only be undone by the organizer (a refund in Stripe), so
  // the member is not offered a way to cancel it away with no money back.
  let has_paid_row = $derived(registrations.some(reg => reg.paid_at));

  // Just paid, while the seats still read as a hold (the webhook is not in
  // yet): the payment is being confirmed, not open. The card shows that —
  // with a spinner, without a pay button or actions — until the entry has
  // the answer and the rows tell the truth again.
  let confirming = $derived(confirming_payment && pending_rows.length > 0);

  // The state chip: what the member most needs to know
  let status = $derived(
    confirming ? { tone: 'neutral', label: t('payment_confirming_title'), busy: true }
    : pending_rows.length > 0 ? { tone: 'warn', label: t('payment_open_title') }
    : all_waitlisted ? { tone: 'neutral', label: t('waitlisted_title') }
    : { tone: 'ok', label: t('registered_title') }
  );

  // Beside the chip: how many seats, what has been paid, what is still open
  let summary = $derived.by(() => {
    const paid_total = registrations
      .filter(reg => reg.paid_at && Number.isInteger(reg.amount_paid))
      .reduce((sum, reg) => sum + reg.amount_paid, 0);
    const unpaid_count = registrations
      .filter(reg => reg.status === 'confirmed' && get_registration_payment_state(reg, entry) === 'unpaid')
      .length;
    return [
      registrations.length === 1 ? t('person_count_singular') : t('people_count', { count: registrations.length }),
      paid_total > 0 ? t('amount_paid_inline', { amount: format_price(paid_total, currency, LANG) }) : '',
      unpaid_count > 0 ? t('guest_list_summary_unpaid', { count: unpaid_count }) : '',
    ].filter(Boolean).join(', ');
  });

  let names = $derived(
    registrations
      .map(reg => !all_waitlisted && reg.status === 'waitlist' ? `${reg.name} (${t('waitlist')})` : reg.name)
      .join(', ')
  );

  let pending_until = $derived.by(() => {
    const expiries = pending_rows.map(reg => reg.pending_expires_at).filter(Boolean).sort();
    return expiries.length > 0 ? format_date(expiries[0], 'HHm') : '';
  });
  let checkout_url = $derived(pending_rows.find(reg => reg.stripe_checkout_url)?.stripe_checkout_url);
  // A hold whose checkout is closed but not yet paid is a payment settling
  // (bank debit): the money is on its way, so the seats cannot be given up.
  let payment_processing = $derived(pending_rows.length > 0 && !checkout_url);

  let max_guests = $derived(entry.max_guests_per_registration || 1);
  // Editing names and reducing guests is allowed even on a postponed or
  // cancelled event (the server permits it); only adding needs a live event.
  // The actions row as a whole is gated on !has_concluded in the template.
  let can_add = $derived(
    !is_restricted && pending_rows.length === 0 && registrations.length < max_guests && capacity.available > 0
  );
  let can_cancel = $derived(!has_concluded && !has_paid_row && !payment_processing);

  // Invoices live in the member's Stripe customer portal, which brings them
  // back to this page. Offered once a Checkout payment that asked for an
  // invoice is paid (rows from before the flag existed read NULL and keep the
  // link), and kept after the event has concluded: the invoice outlives the seat.
  let has_invoice = $derived(registrations.some(reg =>
    reg.paid_at && reg.payment_method === 'stripe' && reg.invoice_requested !== 0
  ));
  let portal_href = $derived(`/create-customer-portal-session?return_to=${encodeURIComponent(page.url.pathname)}`);

  // The next step, in one muted line. A pending hold with no checkout link is a
  // payment still settling (bank debit); the seat is held, nothing more to do.
  let note = $derived(
    confirming ? t('payment_confirming_note')
    : pending_rows.length > 0 && checkout_url && pending_until ? t('payment_open_until', { time: pending_until })
    : pending_rows.length > 0 && !checkout_url ? t('payment_processing_note')
    : has_paid_row ? t('paid_registration_cancel_note')
    : all_waitlisted ? t('waitlist_position_note')
    : can_cancel ? t('registration_cancel_until_start')
    : ''
  );

  function start_edit() {
    edit_rows = registrations.map(reg => ({
      event_registration_id: reg.event_registration_id,
      name: reg.name,
      email: reg.email ?? '',
      original: { name: reg.name, email: reg.email ?? '' },
      remove: false,
      // A paid or mid-payment seat cannot be removed here (see has_paid_row)
      locked: Boolean(reg.paid_at) || reg.status === 'pending',
      locked_label: reg.paid_at ? t('payment_state_paid') : reg.status === 'pending' ? t('payment_open_title') : '',
      errors: {},
    }));
    error = '';
    mode = 'edit';
  }

  function validate_edit_row(row, { report_empty = true } = {}) {
    const errors = row.remove ? {} : get_attendee_errors(row, { report_empty });
    row.errors = translate_attendee_errors(errors, t);
    return Object.keys(errors).length === 0;
  }

  async function apply(changes, counts) {
    busy = true;
    error = '';
    const result = await submit_registration_changes(entry.entry_id, changes);
    busy = false;
    if (!result.success) {
      error = t(result.error);
      // A multi-step change can fail after part of it committed (a cancel that
      // went through, then a failed edit); pull fresh server truth so the card
      // stops showing a guest that is already gone.
      await onrefresh?.();
      return;
    }
    mode = 'view';
    onchanged?.(counts);
  }

  async function save_edit() {
    if (!edit_rows.map(validate_edit_row).every(Boolean)) return;
    const cancelled_ids = edit_rows.filter(row => row.remove).map(row => row.event_registration_id);
    const changed = edit_rows
      .filter(row => !row.remove && (row.name.trim() !== row.original.name || row.email.trim() !== row.original.email))
      .map(row => ({ event_registration_id: row.event_registration_id, name: row.name.trim(), email: row.email.trim() }));
    if (cancelled_ids.length === 0 && changed.length === 0) {
      mode = 'view';
      return;
    }
    await apply({ cancelled_ids, changed }, { changed: changed.length, cancelled: cancelled_ids.length });
  }

  async function cancel_all() {
    const cancelled_ids = registrations.map(reg => reg.event_registration_id);
    await apply({ cancelled_ids }, { cancelled: cancelled_ids.length });
  }

  // Abandon only the unpaid hold. A member with a paid seat cannot cancel the
  // whole registration (has_paid_row), so without this a hold started after
  // paying would have no exit but waiting for it to expire.
  async function cancel_pending() {
    const cancelled_ids = pending_rows.map(reg => reg.event_registration_id);
    if (cancelled_ids.length === 0) return;
    await apply({ cancelled_ids }, { cancelled: cancelled_ids.length });
  }
</script>

<div class='event-lines'>

  <div class='event-compact event-facts'>
    <EventChip tone={status.tone}>
      {#if status.busy}<span class='loading-spinner' aria-hidden='true'></span>{/if}
      {status.label}
    </EventChip>
    <span class='text-control leading-trim'>{summary}</span>
  </div>

  {#if mode === 'edit'}
    <!---------------------------------------------------------------------------------->
    <!-------------------------- Edit names and e-mails -------------------------------->
    <!---------------------------------------------------------------------------------->
    <form class='event-lines' onsubmit={(event) => { event.preventDefault(); save_edit(); }} novalidate>
      <div class='event-compact flex-column'>
        {#each edit_rows as row, index (row.event_registration_id)}
          <fieldset class='event-guest' disabled={busy}>
            <div class='event-guest-heading justify-between'>
              <span class='text-control event-label'>{index === 0 ? t('you') : t('guest_numbered', { number: index })}</span>
              {#if edit_rows.length > 1 && !row.locked}
                <button type='button' onclick={() => { row.remove = !row.remove; row.errors = {}; }}>
                  <ButtonLabel variant={`brand ${site_settings.secondary_control_surface}`}>{row.remove ? t('keep') : t('remove')}</ButtonLabel>
                </button>
              {:else if row.locked}
                <span class='text-control text-2 size-reduce-1'>{row.locked_label}</span>
              {/if}
            </div>
            {#if row.remove}
              <p class='text-control text-2 removed-guest leading-trim'>{row.original.name}</p>
            {:else}
              <TextInput
                id='edit_name_{row.event_registration_id}'
                class='event-field w-full'
                variant='brand ghostly'
                bind:value={row.name}
                placeholder={t('name')}
                aria_label={`${index === 0 ? t('you') : t('guest_numbered', { number: index })}: ${t('name')}`}
                autocomplete='name'
                onblur={() => validate_edit_row(row, { report_empty: false })}
                error={row.errors.name}
              />
              <TextInput
                id='edit_email_{row.event_registration_id}'
                class='event-field w-full'
                variant='brand ghostly'
                type='email'
                bind:value={row.email}
                placeholder={t('email_optional')}
                aria_label={`${index === 0 ? t('you') : t('guest_numbered', { number: index })}: ${t('email')}`}
                autocomplete='email'
                inputmode='email'
                onblur={() => validate_edit_row(row, { report_empty: false })}
                error={row.errors.email}
              />
            {/if}
          </fieldset>
        {/each}
      </div>
      {#if error}
        <p class='text-control text-error leading-trim'>{error}</p>
      {/if}
      <button type='submit' class='full-width' disabled={busy}>
        <ButtonLabel variant={`brand ${site_settings.primary_control_surface}`} class='full-width'>
          {busy ? t('saving') : t('save')}
        </ButtonLabel>
      </button>
      <div class='event-compact event-actions'>
        <button type='button' onclick={() => { mode = 'view'; }} disabled={busy}>
          <ButtonLabel variant={`brand ${site_settings.secondary_control_surface}`}>{t('cancel')}</ButtonLabel>
        </button>
      </div>
    </form>

  {:else if mode === 'add'}
    <!---------------------------------------------------------------------------------->
    <!-------------------------- Add guests -------------------------------------------->
    <!---------------------------------------------------------------------------------->
    <EventRegistrationForm
      {entry}
      {capacity}
      existing_count={registrations.length}
      first_email_optional
      onsubmitted={(counts) => { mode = 'view'; onchanged?.(counts); }}
      oncancel={() => { mode = 'view'; }}
    />

  {:else if mode === 'confirm_cancel'}
    <!---------------------------------------------------------------------------------->
    <!-------------------------- Confirm cancelling everything ------------------------->
    <!---------------------------------------------------------------------------------->
    <p class='text-control leading-trim'>{names}</p>
    <div class='event-prompt'>
      <p class='text-control text-2 leading-trim'>{t('confirm_cancel_my_registration')}</p>
      {#if error}
        <p class='text-control text-error leading-trim'>{error}</p>
      {/if}
      <div class='event-compact event-actions'>
        <button type='button' onclick={() => { mode = 'view'; }} disabled={busy}>
          <ButtonLabel variant={`brand ${site_settings.secondary_control_surface}`}>{t('keep')}</ButtonLabel>
        </button>
        <button type='button' onclick={cancel_all} disabled={busy}>
          <ButtonLabel variant={`system-destructive ${site_settings.primary_control_surface}`}>
            {busy ? t('saving') : (all_waitlisted ? t('leave_waitlist') : t('cancel_my_registration'))}
          </ButtonLabel>
        </button>
      </div>
    </div>

  {:else}
    <!---------------------------------------------------------------------------------->
    <!-------------------------- Guests and actions ------------------------------------>
    <!---------------------------------------------------------------------------------->
    <p class='text-control leading-trim'>{names}</p>
    {#if note}
      <p class='text-control text-2 leading-trim'>{note}</p>
    {/if}
    {#if error}
      <p class='text-control text-error leading-trim'>{error}</p>
    {/if}
    {#if checkout_url && !confirming}
      <a href={checkout_url} class='full-width'>
        <ButtonLabel variant={`brand ${site_settings.primary_control_surface}`} class='full-width'>
          {t('complete_payment')}
        </ButtonLabel>
      </a>
    {/if}
    {#if !has_concluded && !confirming}
      <div class='event-compact event-actions'>
        <button type='button' onclick={start_edit}>
          <ButtonLabel variant={`brand ${site_settings.primary_control_surface}`}>{t('change_guests')}</ButtonLabel>
        </button>
        {#if can_add}
          <button type='button' onclick={() => { error = ''; mode = 'add'; }}>
            <ButtonLabel variant={`brand ${site_settings.primary_control_surface}`}>{t('add_guests')}</ButtonLabel>
          </button>
        {/if}
        {#if can_cancel}
          <button type='button' onclick={() => { error = ''; mode = 'confirm_cancel'; }}>
            <ButtonLabel variant={`brand ${site_settings.primary_control_surface}`}>{t('cancel_short')}</ButtonLabel>
          </button>
        {/if}
        {#if pending_rows.length > 0 && has_paid_row && !payment_processing}
          <button type='button' onclick={cancel_pending} disabled={busy}>
            <ButtonLabel variant={`brand ${site_settings.secondary_control_surface}`}>{busy ? t('saving') : t('cancel_payment')}</ButtonLabel>
          </button>
        {/if}
        {#if has_invoice}
          {@render invoices_link()}
        {/if}
      </div>
    {:else if has_invoice && !confirming}
      <div class='event-compact event-actions'>
        {@render invoices_link()}
      </div>
    {/if}
  {/if}
</div>

<!-- Every visit opens a portal session, so the link must not be preloaded -->
{#snippet invoices_link()}
  <a href={portal_href} data-sveltekit-preload-data='false' data-sveltekit-reload>
    <ButtonLabel variant={`brand ${site_settings.secondary_control_surface}`}>{t('invoices')}</ButtonLabel>
  </a>
{/snippet}

<style lang='postcss'>
  /* The struck name keeps the height of the field it replaces */
  .removed-guest {
    padding-block: calc((var(--input-min-height) - 1cap) / 2);
    text-decoration: line-through;
  }
</style>
